import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Menu as DropdownMenu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  TablePagination,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import PageLayout from '../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import { extractList } from '../utils/response';
import {
  getDBAs,
  getDbaStatus,
  deleteDba,
  updateDbaProcessStatus,
  assignDbaToUser,
} from '../store/slices/merchantsSlice';
import { getUsersList } from '../store/slices/userSlice';
import { toast } from 'react-toastify';
import SweetAlert from '../utils/sweetAlert';
import { useAuth } from '../hooks/useAuth';
import Can from '../components/permissions/Can';

const Pending = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();


  // permission 
  const canViewMerchants = hasPermission('dba-view');
  const canEditMerchants = hasPermission('dba-edit');
  const canChangeStatus = hasPermission('dba-status');
  const canAssignDBA = hasPermission('dba-reassign');
  const canDeleteMerchants = hasPermission('dba-delete');


  const [dbaData, setDbaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedDba, setSelectedDba] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!canViewMerchants) {
        toast.error('You do not have permission to view merchants.');
        return;
      }
      try {
        setLoading(true);
        const params = { 'conditions[model]': 'dba' };
        const response = await dispatch(getDbaStatus(params)).unwrap();
        setStatusOptions(extractList(response));
      } catch (error) {
        console.error('Error fetching status options:', error);
        toast.error('Failed to load status options.');
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      if (!canAssignDBA) return;
      try {
        const params = { 'relations[]': ['status', 'roles'], paginate: 0 };
        const userData = await dispatch(getUsersList(params)).unwrap();
        setUsersList(extractList(userData));
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users.');
      }
    };

    fetchData();
    fetchUsers();
  }, [canViewMerchants, canAssignDBA]);

  const loadDBAData = async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
    if (!canViewMerchants) {
      toast.error('You do not have permission to view merchants.');
      return;
    }
    try {
      setTableLoading(true);
      const params = {
        'relations[]': ['legalInformation', 'merchant', 'bankAccount', 'dbaOwners', 'equipments', 'attributes', 'lastStatusLog', 'users'],
        'conditions[legal_info_id]': '',
        'conditions[type]': 'normal',
        'status': 'pending',
        paginate: 1,
        page: page,
        per_page: pageSize,
        ...(search && { 'search': search })
      };
      const response = await dispatch(getDBAs(params)).unwrap();
      setDbaData(response?.data?.list ?? response?.list ?? []);
      setPagination(prev => ({
        ...prev,
        total: response?.data?.pagination?.total ?? response?.pagination?.total ?? 0
      }));
    } catch (error) {
      console.error('Error loading DBA data:', error);
      toast.error('Failed to load DBA data. Please try again.');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadDBAData();
  }, [searchValue, pagination.page, pagination.pageSize, selectedUser, newStatus]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== '') {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadDBAData(1, pagination.pageSize, searchValue);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchValue('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, total: pagination.total });
  };

  const handleView = (dba) => {
    navigate(`/view-merchant?id=${dba.id}`);
  };

  const handleEdit = (dba) => {
    navigate(`/create-merchant?edit=true&id=${dba.id}`);
  };


  const handleDelete = async (dba) => {
    if (!canDeleteMerchants) {
      toast.error('You do not have permission to delete merchants.');
      return;
    }
    try {
      // Show SweetAlert confirmation
      const result = await SweetAlert.deleteConfirm(dba.name || 'this DBA');

      // If user cancels, return early
      if (!result.isConfirmed) {
        return;
      }

      setProcessingId(dba.id);
      await dispatch(deleteDba(dba.id)).unwrap();
      await SweetAlert.success({
        text: 'DBA record deleted successfully.'
      });
      await loadDBAData();
    } catch (error) {
      console.error('Delete action error:', error);
      await SweetAlert.error({
        text: 'Failed to delete DBA record'
      });
    } finally {
      setProcessingId(null);
    }
  };


  const handleOpenStatusDialog = (dba) => {
    setSelectedDba(dba);
    setNewStatus(dba.status?.id || '');
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedDba(null);
    setNewStatus('');
  };

  const handleChangeStatus = async () => {
    if (!canChangeStatus) {
      toast.error('You do not have permission to change merchant status.');
      return;
    }
    if (!selectedDba || !newStatus) {
      toast.warn('Please select a status.');
      return;
    }

    try {
      setProcessingId(selectedDba.id);
      const response = await dispatch(updateDbaProcessStatus({ id: selectedDba.id, statusId: newStatus })).unwrap();

      if (response?.success) {
        setDbaData((prev) =>
          prev.map((dba) =>
            dba.id === selectedDba.id
              ? {
                ...dba,
                status: {
                  ...dba.status,
                  id: newStatus,
                  name: statusOptions.find((option) => option.id === newStatus)?.name || dba.status?.name
                }
              }
              : dba
          )
        );

        toast.success('Status updated successfully');
        handleCloseStatusDialog();
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssignDBA) {
      toast.error('You do not have permission to assign merchants.');
      return;
    }
    if (!selectedDba || !selectedUser) {
      toast.warn('Please select a user.');
      return;
    }

    try {
      setProcessingId(selectedDba.id);
      const response = await dispatch(assignDbaToUser({ id: selectedDba.id, userId: selectedUser })).unwrap();

      if (response.success) {
        const assignedUser = usersList.find(user => user.id === selectedUser);

        setDbaData((prev) =>
          prev.map((dba) =>
            dba.id === selectedDba.id
              ? { ...dba, users: { ...dba.users?.name, name: assignedUser?.name || 'Assigned' } }
              : dba
          )
        );

        toast.success('DBA assigned successfully');
        setOpenAssignDialog(false);
        setSelectedUser('');
      } else {
        throw new Error(response.message || 'Failed to assign DBA');
      }
    } catch (error) {
      console.error('Error assigning DBA:', error);
      toast.error('Failed to assign DBA. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {loading ? (
            <Skeleton variant="text" width={200} height={40} />
          ) : (
            <Typography
              variant="h4"
              sx={{ color: '#154A8A', fontWeight: 600 }}
            >
              Pending
            </Typography>
          )}

          {loading ? (
            <Skeleton variant="rectangular" width={300} height={40} />
          ) : (
            <TextField
              placeholder="Search by Merchant DBA"
              value={searchValue}
              onChange={handleSearchChange}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        <Paper sx={{ boxShadow: 1 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    Merchant DBA
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    Bank Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    DBA Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    Assigned To
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    Date Stamp
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'grey.700', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  // Skeleton loading rows
                  [...Array(pagination.pageSize)]?.map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                      <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                      <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                      <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                      <TableCell><Skeleton animation="wave" height={30} /></TableCell>
                      <TableCell><Skeleton animation="wave" height={30} width={80} /></TableCell>
                      <TableCell sx={{ textAlign: 'center' }}><Skeleton animation="wave" height={30} width={40} /></TableCell>
                    </TableRow>
                  ))
                ) : dbaData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchValue ? 'No records found for your search' : "There's no records to display"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dbaData?.map((dba) => (
                    <TableRow key={dba.id} hover>
                      <TableCell>{dba.name || 'N/A'}</TableCell>
                      <TableCell>{dba?.bank_account?.bank_name || 'N/A'}</TableCell>
                      <TableCell>{dba?.email || 'N/A'}</TableCell>
                      <TableCell>{dba.users[0]?.name || 'Unassigned'}</TableCell>
                      <TableCell>{new Date(dba.created_at || dba.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip
                          sx={{ textTransform: 'capitalize' }}
                          label={dba?.last_status_log?.status?.name || 'Pending'}
                          color={dba?.last_status_log?.status?.name === 'Completed' ? 'success' : dba?.last_status_log?.status?.name === 'Underwriting' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Can permission="dba-view" fallback={<Box sx={{ width: 40, display: 'inline-block' }} />}>
                          <IconButton
                            onClick={(event) => {
                              setSelectedDba(dba);
                              setAnchorEl(event.currentTarget);
                            }}
                            disabled={processingId === dba.id}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!tableLoading && dbaData.length > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={(_, newPage) => {
                handlePageChange(newPage + 1);
              }}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows per page"
            />
          )}
        </Paper>

        <DropdownMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <Can permission="dba-view">
            <MenuItem onClick={() => {
              handleView(selectedDba);
              setAnchorEl(null);
            }}>
              View
            </MenuItem>
          </Can>

          <Can permission="dba-edit">
            <MenuItem onClick={() => {
              handleEdit(selectedDba);
              setAnchorEl(null);
            }}>
              Edit
            </MenuItem>
          </Can>

          <Can permission="dba-status">
            <MenuItem onClick={() => {
              handleOpenStatusDialog(selectedDba);
              setAnchorEl(null);
            }}>
              Change Status
            </MenuItem>
          </Can>

          <Can permission="dba-reassign">
            <MenuItem onClick={() => {
              setOpenAssignDialog(true);
              setAnchorEl(null);
            }}>
              Assign DBA to User
            </MenuItem>
          </Can>

          <Can permission="dba-delete">
            <MenuItem onClick={() => {
              handleDelete(selectedDba);
              setAnchorEl(null);
            }}>
              Delete
            </MenuItem>
          </Can>
        </DropdownMenu>

        <Dialog
          open={openStatusDialog}
          onClose={handleCloseStatusDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              p: 2
            }
          }}
        >
          <DialogTitle sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#154A8A',
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.200'
          }}>
            Change DBA Status
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
              Update the status for {selectedDba?.name || 'this DBA'}. Select a new status below.
            </DialogContentText>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Change the DBA status to</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Change the DBA status to"
                sx={{ bgcolor: 'white', borderRadius: 1, textTransform: 'capitalize' }}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name.charAt(0).toUpperCase() + status.name.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={handleCloseStatusDialog}
              variant="outlined"
              color="secondary"
              sx={{
                borderColor: 'grey.400',
                color: 'grey.700',
                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeStatus}
              variant="contained"
              color="primary"
              disabled={!newStatus || processingId === selectedDba?.id}
              startIcon={processingId === selectedDba?.id ? <CircularProgress size={20} /> : null}
              sx={{
                bgcolor: '#154A8A',
                '&:hover': { bgcolor: '#0d3a6a' },
                '&:disabled': { bgcolor: '#154A8A', opacity: 0.5 }
              }}
            >
              Change Status
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openAssignDialog}
          onClose={() => setOpenAssignDialog(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              p: 2
            }
          }}
        >
          <DialogTitle sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#154A8A',
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.200'
          }}>
            Assign DBA to User
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
              Assign {selectedDba?.name || 'this DBA'} to a user. Search and select a user below.
            </DialogContentText>

            <Autocomplete
              options={usersList}
              getOptionLabel={(option) => option.name}
              value={usersList.find(user => user.id === selectedUser) || null}
              onChange={(event, newValue) => {
                setSelectedUser(newValue?.id || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User"
                  variant="outlined"
                  placeholder="Type to search..."
                  sx={{ bgcolor: 'white' }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  {option.name}
                </Box>
              )}
              sx={{ mt: 1 }}
              fullWidth
              disablePortal={false}
              clearOnEscape
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setOpenAssignDialog(false)}
              variant="outlined"
              color="secondary"
              sx={{
                borderColor: 'grey.400',
                color: 'grey.700',
                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              variant="contained"
              color="primary"
              disabled={!selectedUser || processingId === selectedDba?.id}
              startIcon={processingId === selectedDba?.id ? <CircularProgress size={20} /> : null}
              sx={{
                bgcolor: '#154A8A',
                '&:hover': { bgcolor: '#0d3a6a' },
                '&:disabled': { bgcolor: '#154A8A', opacity: 0.5 }
              }}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageLayout>
  );
};

export default Pending;